import React, { useState } from 'react'
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Tag, 
  DatePicker, 
  Select, 
  Input, 
  Row, 
  Col,
  Statistic,
  Tooltip,
  Descriptions,
  Drawer,
  Tabs,
  Alert,
  Pagination,
  Spin,
  Empty,
} from 'antd'
import { 
  SearchOutlined, 
  ReloadOutlined, 
  EyeOutlined,
  DownloadOutlined,
  FilterOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  BarChartOutlined
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import type { ColumnsType } from 'antd/es/table'
import { api } from '@services/api'
import { useIsMobile } from '@hooks/useBreakpoint'
import TradeRecordCard from '@components/trade/TradeRecordCard'
import type { TradeRecordCardData } from '@components/trade/TradeRecordCard'

const { RangePicker } = DatePicker
const { Option } = Select
const { Search } = Input

// 完整交易记录接口
interface TradeRecord {
  id: number
  signal_id?: string
  token_name?: string
  symbol: string
  side: 'buy' | 'sell'
  order_type: string
  
  // 仓位信息
  contracts: number
  entry_price: number
  /** 开仓名义价值（报价币种，USDT 本位合约下为 USDT） */
  position_value_usdt?: number | null
  exit_price?: number
  avg_price?: number
  
  // 时间信息
  opened_at: string
  closed_at?: string
  holding_hours?: number
  
  // 盈亏信息
  realized_pnl: number
  unrealized_pnl?: number
  commission?: number
  open_fee_type?: 'maker' | 'taker' | 'mixed' | 'unknown'
  close_fee_type?: 'maker' | 'taker' | 'mixed' | 'unknown' | null
  net_pnl: number
  
  // 风险信息
  leverage?: number
  margin?: number
  max_profit?: number
  max_loss?: number
  
  // 其他信息
  exchange: string
  status: 'open' | 'closed' | 'liquidated' | 'unmatched'
  close_reason?: string
}

// 筛选参数
interface TradeFilters {
  symbol?: string
  side?: string
  status?: string
  exchange?: string
  dateRange?: [string, string]
  minPnl?: number
  maxPnl?: number
  tokenName?: string
}

const toNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : fallback
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : fallback
  }
  return fallback
}

const formatPrice = (value: unknown): string => {
  const n = toNumber(value, NaN)
  if (!Number.isFinite(n)) return '-'

  const abs = Math.abs(n)
  // Crypto prices often need more than 2 decimals (e.g. XRP ~ 1.x).
  // Keep it readable but precise enough for trading.
  const decimals = abs >= 1000 ? 2 : abs >= 100 ? 3 : abs >= 1 ? 4 : 6
  return `$${n.toFixed(decimals)}`
}

const normalizeTradeSideParam = (side?: string): string | undefined => {
  if (!side) return undefined
  const normalized = side.toLowerCase()
  if (normalized === 'buy') return 'long'
  if (normalized === 'sell') return 'short'
  return normalized
}

const getSideMeta = (side: unknown, status?: unknown) => {
  const normalized = String(side || '').toLowerCase()
  const st = String(status || '').toLowerCase()
  if (normalized === 'unmatched' || st === 'unmatched') {
    return { color: 'orange', label: '未配对' }
  }
  if (normalized === 'buy' || normalized === 'long') return { color: 'green', label: '做多' }
  if (normalized === 'sell' || normalized === 'short') return { color: 'red', label: '做空' }
  return { color: 'default', label: normalized || '-' }
}

const buildTradeHistoryParams = (
  filters: TradeFilters,
  current: number,
  pageSize: number,
  includePagination = true
) => {
  const { dateRange, tokenName, side, ...rest } = filters
  const params: Record<string, any> = {
    ...rest,
    side: normalizeTradeSideParam(side),
  }
  if (includePagination) {
    params.skip = (current - 1) * pageSize
    params.limit = pageSize
  }

  if (tokenName) params.token_name = tokenName
  if (dateRange && dateRange.length === 2) {
    params.start_date = dateRange[0]
    params.end_date = dateRange[1]
  }

  return Object.keys(params).reduce<Record<string, any>>((acc, key) => {
    const value = params[key]
    if (value !== undefined && value !== null && value !== '') {
      acc[key] = value
    }
    return acc
  }, {})
}

type HistoryTab = 'db' | 'exchange'

const TradeHistory: React.FC = () => {
  const isMobile = useIsMobile()
  const [activeTab, setActiveTab] = useState<HistoryTab>('db')
  const [filters, setFilters] = useState<TradeFilters>({})
  const [selectedTrade, setSelectedTrade] = useState<TradeRecord | null>(null)
  const [detailVisible, setDetailVisible] = useState(false)
  const [pageSize, setPageSize] = useState(50)
  const [current, setCurrent] = useState(1)

  const { data: tokens } = useQuery({
    queryKey: ['tokens-list-for-history'],
    queryFn: () => api.token.list(),
  })

  const historyParams = buildTradeHistoryParams(filters, current, pageSize)
  const summaryParams = buildTradeHistoryParams(filters, 1, pageSize, false)

  const { data: dbTradeData, isLoading: dbLoading, refetch: refetchDb } = useQuery({
    queryKey: ['trade-history', 'db', filters, current, pageSize],
    queryFn: () => api.tradeHistory.getTradeHistory(historyParams),
    refetchInterval: activeTab === 'db' ? 30000 : false,
    enabled: activeTab === 'db',
  })

  const { data: exchangeTradeData, isLoading: exchangeLoading, refetch: refetchExchange } = useQuery({
    queryKey: ['trade-history', 'exchange', filters, current, pageSize],
    queryFn: () => api.tradeHistory.getExchangeTradeHistory(historyParams),
    refetchInterval: activeTab === 'exchange' ? 60000 : false,
    enabled: activeTab === 'exchange',
  })

  const { data: dbSummary } = useQuery({
    queryKey: ['trade-history-summary', 'db', filters],
    queryFn: () => api.tradeHistory.getTradeHistorySummary(summaryParams),
    enabled: activeTab === 'db',
  })

  const { data: exchangeSummary } = useQuery({
    queryKey: ['trade-history-summary', 'exchange', filters],
    queryFn: () => api.tradeHistory.getExchangeTradeHistorySummary(summaryParams),
    enabled: activeTab === 'exchange',
  })

  const tradeData = activeTab === 'db' ? dbTradeData : exchangeTradeData
  const isLoading = activeTab === 'db' ? dbLoading : exchangeLoading
  const summary = activeTab === 'db' ? dbSummary : exchangeSummary
  const refetch = activeTab === 'db' ? refetchDb : refetchExchange

  // 格式化货币
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value)
  }

  // 格式化时间
  const formatDuration = (hours: number) => {
    if (hours < 1) return `${Math.round(hours * 60)}分钟`
    if (hours < 24) return `${hours.toFixed(1)}小时`
    return `${(hours / 24).toFixed(1)}天`
  }

  // 获取PnL颜色
  const getPnlColor = (pnl: number) => {
    if (pnl > 0) return '#3f8600'
    if (pnl < 0) return '#cf1322'
    return '#8c8c8c'
  }

  // 表格列定义
  const columns: ColumnsType<TradeRecord> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      fixed: 'left',
    },
    {
      title: '交易对',
      dataIndex: 'symbol',
      key: 'symbol',
      width: 100,
      fixed: 'left',
      render: (symbol) => (
        <Tag color="blue" style={{ fontFamily: 'monospace' }}>{symbol}</Tag>
      ),
    },
    {
      title: '方向',
      dataIndex: 'side',
      key: 'side',
      width: 80,
      render: (side, record) => {
        const sideMeta = getSideMeta(side, record.status)
        return <Tag color={sideMeta.color}>{sideMeta.label}</Tag>
      },
    },
    {
      title: '数量',
      dataIndex: 'contracts',
      key: 'contracts',
      width: 100,
      align: 'right',
      render: (contracts) => toNumber(contracts).toFixed(4),
    },
    {
      title: '开仓价',
      dataIndex: 'entry_price',
      key: 'entry_price',
      width: 100,
      align: 'right',
      render: (price) => formatPrice(price),
    },
    {
      title: '仓位价值(USDT)',
      dataIndex: 'position_value_usdt',
      key: 'position_value_usdt',
      width: 120,
      align: 'right',
      render: (v) => {
        const n = toNumber(v, NaN)
        return Number.isFinite(n) && n > 0 ? formatCurrency(n) : '-'
      },
    },
    {
      title: '平仓价',
      dataIndex: 'exit_price',
      key: 'exit_price',
      width: 100,
      align: 'right',
      render: (price) => {
        return formatPrice(price)
      },
    },
    {
      title: '开仓时间',
      dataIndex: 'opened_at',
      key: 'opened_at',
      width: 150,
      render: (date) => dayjs(date).format('MM-DD HH:mm:ss'),
    },
    {
      title: '平仓时间',
      dataIndex: 'closed_at',
      key: 'closed_at',
      width: 150,
      render: (date) => date ? dayjs(date).format('MM-DD HH:mm:ss') : '-',
    },
    {
      title: '持仓时长',
      dataIndex: 'holding_hours',
      key: 'holding_hours',
      width: 100,
      align: 'center',
      render: (hours) => toNumber(hours, NaN) ? (
        <Tooltip title={`${toNumber(hours).toFixed(2)} 小时`}>
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ClockCircleOutlined style={{ marginRight: 4 }} />
            {formatDuration(toNumber(hours))}
          </span>
        </Tooltip>
      ) : '-',
    },
    {
      title: '已实现盈亏',
      dataIndex: 'realized_pnl',
      key: 'realized_pnl',
      width: 120,
      align: 'right',
      render: (pnl) => (
        <span 
          style={{ 
            color: getPnlColor(toNumber(pnl)), 
            fontWeight: 'bold',
            fontFamily: 'monospace'
          }}
        >
          {formatCurrency(toNumber(pnl))}
        </span>
      ),
      sorter: (a, b) => toNumber(a.realized_pnl) - toNumber(b.realized_pnl),
    },
    {
      title: '手续费',
      dataIndex: 'commission',
      key: 'commission',
      width: 80,
      align: 'right',
      render: (commission) => {
        const numericCommission = toNumber(commission, NaN)
        return Number.isFinite(numericCommission) ? formatCurrency(numericCommission) : '-'
      },
    },
    {
      title: '手续费类型(开/平)',
      key: 'fee_type',
      width: 140,
      align: 'center',
      render: (_, record) => {
        const open = (record.open_fee_type || 'unknown') as string
        const close = (record.close_fee_type || (record.status === 'closed' ? 'unknown' : '-')) as string

        const toTag = (v: string) => {
          const normalized = String(v || '').toLowerCase()
          if (normalized === 'maker') return <Tag color="green">Maker</Tag>
          if (normalized === 'taker') return <Tag color="red">Taker</Tag>
          if (normalized === 'mixed') return <Tag color="purple">Mixed</Tag>
          if (normalized === '-' || normalized === 'none') return <Tag>-</Tag>
          return <Tag color="default">Unknown</Tag>
        }

        return (
          <Space size={4} direction="vertical">
            <span>开 {toTag(open)}</span>
            <span>平 {toTag(close)}</span>
          </Space>
        )
      },
    },
    {
      title: '净盈亏',
      dataIndex: 'net_pnl',
      key: 'net_pnl',
      width: 120,
      align: 'right',
      render: (pnl) => (
        <span 
          style={{ 
            color: getPnlColor(toNumber(pnl)), 
            fontWeight: 'bold',
            fontSize: '14px',
            fontFamily: 'monospace'
          }}
        >
          {formatCurrency(toNumber(pnl))}
        </span>
      ),
      sorter: (a, b) => toNumber(a.net_pnl) - toNumber(b.net_pnl),
    },
    {
      title: '杠杆',
      dataIndex: 'leverage',
      key: 'leverage',
      width: 80,
      align: 'center',
      render: (leverage) => {
        const numericLeverage = toNumber(leverage, NaN)
        return Number.isFinite(numericLeverage) ? `${numericLeverage}x` : '-'
      },
    },
    {
      title: '交易所',
      dataIndex: 'exchange',
      key: 'exchange',
      width: 80,
      render: (exchange) => (
        <Tag color="purple" style={{ textTransform: 'uppercase' }}>
          {exchange}
        </Tag>
      ),
    },
    {
      title: 'Token',
      dataIndex: 'token_name',
      key: 'token_name',
      width: 120,
      ellipsis: true,
      render: (name) => name || '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status) => {
        const colors = {
          open: 'processing',
          closed: 'success',
          liquidated: 'error',
          unmatched: 'warning',
        }
        const labels = {
          open: '持仓中',
          closed: '已平仓',
          liquidated: '已清算',
          unmatched: '未配对',
        }
        const normalizedStatus = String(status || '').toLowerCase() as keyof typeof colors
        return <Tag color={colors[normalizedStatus] || 'default'}>{labels[normalizedStatus] || String(status || '-')}</Tag>
      },
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => {
                setSelectedTrade(record)
                setDetailVisible(true)
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ]

  const handleSearch = (values: any) => {
    setFilters(values)
    setCurrent(1)
  }

  const clearFilters = () => {
    setFilters({})
    setCurrent(1)
  }

  const exportData = () => {
    // 导出功能
    console.log('导出交易历史数据')
  }

  const handleTabChange = (key: string) => {
    setActiveTab(key as HistoryTab)
    setCurrent(1)
  }

  const records = (tradeData?.records || []) as TradeRecord[]
  const paginationConfig = {
    current,
    pageSize,
    total: tradeData?.total || 0,
    showSizeChanger: !isMobile,
    showQuickJumper: !isMobile,
    simple: isMobile,
    showTotal: (total: number, range: [number, number]) =>
      `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
    onChange: (page: number, size?: number) => {
      setCurrent(page)
      setPageSize(size || 50)
    },
    pageSizeOptions: ['20', '50', '100', '200'],
  }

  const openDetail = (record: TradeRecordCardData) => {
    setSelectedTrade(record as TradeRecord)
    setDetailVisible(true)
  }

  return (
    <div className="trade-history-page">
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        style={{ marginBottom: 16 }}
        size={isMobile ? 'small' : 'middle'}
        items={[
          {
            key: 'db',
            label: isMobile ? '跟单记录' : '跟单记录（数据库）',
          },
          {
            key: 'exchange',
            label: isMobile ? '交易所' : '交易所成交',
          },
        ]}
      />

      {activeTab === 'db' && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="关于「未配对」"
          description={
            isMobile
              ? '未能配对的订单标记为「未配对」，可与「交易所」栏对照。'
              : '本栏按 webhook 订单顺序配对。未能配对的买入或卖出会标记为「未配对」，不再显示为做空。可与「交易所成交」栏对照核实。'
          }
        />
      )}

      {activeTab === 'exchange' && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="数据来源：交易所"
          description={
            isMobile
              ? '从交易所拉取成交并配对。OKX 近3个月内数据，加载可能较慢。'
              : '本栏从交易所拉取成交（OKX 自动向后翻页 + 近3个月归档成交），按订单合并后配对为开平仓记录。历史超过3个月需到 OKX 后台导出。加载较慢属正常。'
          }
        />
      )}

      {/* 汇总统计 */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]}>
          <Col xs={12} sm={6}>
            <Statistic
              title="已平仓 / 持仓中"
              value={summary?.total_trades || 0}
              suffix={`/ ${summary?.open_positions ?? 0}${(summary?.unmatched_positions ?? 0) > 0 ? ` / 未配对 ${summary.unmatched_positions}` : ''}`}
              prefix={<BarChartOutlined />}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="总净盈亏"
              value={toNumber(summary?.total_net_pnl)}
              precision={2}
              prefix={<DollarOutlined />}
              valueStyle={{ color: getPnlColor(toNumber(summary?.total_net_pnl)) }}
              suffix="USD"
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="胜率"
              value={toNumber(summary?.win_rate)}
              precision={1}
              suffix="%"
              valueStyle={{ 
                color: toNumber(summary?.win_rate) > 50 ? '#3f8600' : '#cf1322'
              }}
            />
          </Col>
          <Col xs={12} sm={6}>
            <Statistic
              title="平均持仓"
              value={formatDuration(toNumber(summary?.avg_holding_hours))}
              prefix={<ClockCircleOutlined />}
            />
          </Col>
        </Row>
      </Card>

      <Card
        title={activeTab === 'db' ? (isMobile ? '跟单历史' : '跟单交易历史（数据库配对）') : isMobile ? '交易所历史' : '交易所成交历史（聚合）'}
        extra={
          <Space wrap size={isMobile ? 4 : 8}>
            {!isMobile && (
              <Button icon={<DownloadOutlined />} onClick={exportData}>
                导出
              </Button>
            )}
            <Button icon={<ReloadOutlined />} onClick={() => refetch()} size={isMobile ? 'small' : 'middle'}>
              {isMobile ? '' : '刷新'}
            </Button>
          </Space>
        }
        styles={isMobile ? { header: { flexWrap: 'wrap', gap: 8 } } : undefined}
      >
        {/* 筛选栏 */}
        <div style={{ marginBottom: 16, padding: 16, background: '#fafafa', borderRadius: 6 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8} md={6}>
              <Search
                placeholder="搜索交易对"
                allowClear
                onSearch={(value) => handleSearch({ ...filters, symbol: value })}
                style={{ width: '100%' }}
              />
            </Col>
            
            <Col xs={24} sm={8} md={6}>
              <Select
                placeholder="交易方向"
                allowClear
                style={{ width: '100%' }}
                onChange={(value) => handleSearch({ ...filters, side: value })}
              >
                <Option value="long">做多</Option>
                <Option value="short">做空</Option>
              </Select>
            </Col>
            
            <Col xs={24} sm={8} md={6}>
              <Select
                placeholder="状态"
                allowClear
                style={{ width: '100%' }}
                onChange={(value) => handleSearch({ ...filters, status: value })}
              >
                <Option value="open">持仓中</Option>
                <Option value="closed">已平仓</Option>
                <Option value="unmatched">未配对</Option>
                <Option value="liquidated">已清算</Option>
              </Select>
            </Col>
            
            <Col xs={24} sm={8} md={6}>
              <Select
                placeholder="交易所"
                allowClear
                style={{ width: '100%' }}
                onChange={(value) => handleSearch({ ...filters, exchange: value })}
              >
                <Option value="binance">Binance</Option>
                <Option value="okx">OKX</Option>
                <Option value="bybit">Bybit</Option>
              </Select>
            </Col>

            <Col xs={24} sm={8} md={6}>
              <Select
                placeholder="Token"
                allowClear
                style={{ width: '100%' }}
                onChange={(value) => handleSearch({ ...filters, tokenName: value })}
              >
                {(tokens || []).map((t: any) => (
                  <Option key={t.id} value={t.name}>{t.name}</Option>
                ))}
              </Select>
            </Col>
            
            <Col xs={24} sm={16} md={8}>
              <RangePicker
                placeholder={['开始时间', '结束时间']}
                style={{ width: '100%' }}
                showTime
                onChange={(dates) => {
                  if (dates) {
                    handleSearch({
                      ...filters,
                      dateRange: [
                        dates[0]?.format('YYYY-MM-DD HH:mm:ss'),
                        dates[1]?.format('YYYY-MM-DD HH:mm:ss')
                      ]
                    })
                  } else {
                    handleSearch({ ...filters, dateRange: undefined })
                  }
                }}
              />
            </Col>
            
            <Col xs={24} sm={8} md={4}>
              <Space>
                <Button 
                  icon={<FilterOutlined />} 
                  onClick={clearFilters}
                >
                  清空
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        {isMobile ? (
          <>
            <Spin spinning={isLoading}>
              {records.length > 0 ? (
                <div className="trade-card-list">
                  {records.map((record) => (
                    <TradeRecordCard
                      key={record.id}
                      record={record}
                      getSideMeta={getSideMeta}
                      formatPrice={formatPrice}
                      formatCurrency={formatCurrency}
                      formatDuration={formatDuration}
                      getPnlColor={getPnlColor}
                      onViewDetail={openDetail}
                    />
                  ))}
                </div>
              ) : (
                !isLoading && <Empty description="暂无记录" style={{ padding: '32px 0' }} />
              )}
            </Spin>
            {(tradeData?.total || 0) > 0 && (
              <Pagination
                className="trade-history-mobile-pagination"
                style={{ marginTop: 16, textAlign: 'center' }}
                {...paginationConfig}
              />
            )}
          </>
        ) : (
          <>
            <p className="table-scroll-hint desktop-only">提示：表格列较多时可横向滑动查看</p>
            <Table
              columns={columns}
              dataSource={records}
              loading={isLoading}
              rowKey="id"
              scroll={{ x: 1920, y: 600 }}
              pagination={paginationConfig}
              size="small"
              rowClassName={(record) => {
                if (record.status === 'liquidated') return 'liquidated-row'
                if (record.net_pnl > 0) return 'profit-row'
                if (record.net_pnl < 0) return 'loss-row'
                return ''
              }}
            />
          </>
        )}
      </Card>

      {/* 交易详情抽屉 */}
      <Drawer
        title={`交易详情 #${selectedTrade?.id}`}
        placement="right"
        width={isMobile ? '100%' : 600}
        open={detailVisible}
        onClose={() => setDetailVisible(false)}
      >
        {selectedTrade && (
          <div>
            <Descriptions title="基本信息" bordered column={isMobile ? 1 : 2} size="small">
              <Descriptions.Item label="交易对">{selectedTrade.symbol}</Descriptions.Item>
              <Descriptions.Item label="方向">
                {(() => {
                  const sideMeta = getSideMeta(selectedTrade.side, selectedTrade.status)
                  return <Tag color={sideMeta.color}>{sideMeta.label}</Tag>
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="数量">{selectedTrade.contracts}</Descriptions.Item>
              <Descriptions.Item label="杠杆">{selectedTrade.leverage}x</Descriptions.Item>
              <Descriptions.Item label="交易所">
                <Tag color="purple">{selectedTrade.exchange.toUpperCase()}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={
                  selectedTrade.status === 'open' ? 'processing' :
                  selectedTrade.status === 'closed' ? 'success' : 'error'
                }>
                  {selectedTrade.status === 'open' ? '持仓中' :
                   selectedTrade.status === 'closed' ? '已平仓' :
                   selectedTrade.status === 'unmatched' ? '未配对' : '已清算'}
                </Tag>
              </Descriptions.Item>
            </Descriptions>

            <Descriptions title="价格信息" bordered column={isMobile ? 1 : 2} size="small" style={{ marginTop: 16 }}>
              <Descriptions.Item label="开仓价">${toNumber(selectedTrade.entry_price).toFixed(4)}</Descriptions.Item>
              <Descriptions.Item label="仓位价值(USDT)">
                {(() => {
                  const n = toNumber(selectedTrade.position_value_usdt, NaN)
                  return Number.isFinite(n) && n > 0 ? formatCurrency(n) : '-'
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="平仓价">
                {selectedTrade.exit_price !== undefined && selectedTrade.exit_price !== null
                  ? `$${toNumber(selectedTrade.exit_price).toFixed(4)}`
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="平均价">
                {selectedTrade.avg_price !== undefined && selectedTrade.avg_price !== null
                  ? `$${toNumber(selectedTrade.avg_price).toFixed(4)}`
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="保证金">{formatCurrency(toNumber(selectedTrade.margin))}</Descriptions.Item>
            </Descriptions>

            <Descriptions title="时间信息" bordered column={isMobile ? 1 : 2} size="small" style={{ marginTop: 16 }}>
              <Descriptions.Item label="开仓时间">
                {dayjs(selectedTrade.opened_at).format('YYYY-MM-DD HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="平仓时间">
                {selectedTrade.closed_at ? 
                  dayjs(selectedTrade.closed_at).format('YYYY-MM-DD HH:mm:ss') : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="持仓时长" span={2}>
                {selectedTrade.holding_hours ? formatDuration(selectedTrade.holding_hours) : '-'}
              </Descriptions.Item>
            </Descriptions>

            <Descriptions title="盈亏信息" bordered column={isMobile ? 1 : 2} size="small" style={{ marginTop: 16 }}>
              <Descriptions.Item label="已实现盈亏">
                <span style={{ color: getPnlColor(selectedTrade.realized_pnl), fontWeight: 'bold' }}>
                  {formatCurrency(selectedTrade.realized_pnl)}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="手续费">
                {formatCurrency(selectedTrade.commission || 0)}
              </Descriptions.Item>
              <Descriptions.Item label="净盈亏">
                <span style={{ 
                  color: getPnlColor(selectedTrade.net_pnl), 
                  fontWeight: 'bold',
                  fontSize: '16px'
                }}>
                  {formatCurrency(selectedTrade.net_pnl)}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="收益率">
                {(() => {
                  const pv = toNumber(selectedTrade.position_value_usdt, NaN)
                  const denom =
                    Number.isFinite(pv) && pv > 0
                      ? pv
                      : toNumber(selectedTrade.entry_price) * toNumber(selectedTrade.contracts)
                  if (!denom || selectedTrade.net_pnl === undefined || selectedTrade.net_pnl === null) {
                    return '-'
                  }
                  return `${((toNumber(selectedTrade.net_pnl) / denom) * 100).toFixed(2)}%`
                })()}
              </Descriptions.Item>
            </Descriptions>

            {(selectedTrade.max_profit || selectedTrade.max_loss) && (
              <Descriptions title="极值信息" bordered column={2} size="small" style={{ marginTop: 16 }}>
                <Descriptions.Item label="最大盈利">
                  {selectedTrade.max_profit ? formatCurrency(selectedTrade.max_profit) : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="最大亏损">
                  {selectedTrade.max_loss ? formatCurrency(selectedTrade.max_loss) : '-'}
                </Descriptions.Item>
              </Descriptions>
            )}

            {selectedTrade.signal_id && (
              <Descriptions title="关联信息" bordered column={2} size="small" style={{ marginTop: 16 }}>
                <Descriptions.Item label="信号ID">{selectedTrade.signal_id}</Descriptions.Item>
                <Descriptions.Item label="Token">{selectedTrade.token_name || '-'}</Descriptions.Item>
              </Descriptions>
            )}
          </div>
        )}
      </Drawer>

      <style>{`
        .profit-row {
          background-color: #f6ffed !important;
        }
        .loss-row {
          background-color: #fff2f0 !important;
        }
        .liquidated-row {
          background-color: #fff1f0 !important;
          border-left: 3px solid #ff4d4f !important;
        }
      `}</style>
    </div>
  )
}

export default TradeHistory

